import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTrip } from '@/hooks/useTrip';
import { useSharedTrip } from '@/hooks/useShareToken';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { NotFound } from '@/components/NotFound';
import { Button } from '@/components/ui/button';
import { TraditionalView, MapView, StoryView } from '@/components/itinerary';
import { ArrowLeft, BookOpen, List, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';

type ItineraryMode = 'story' | 'timeline' | 'map';

export const Route = createFileRoute('/trip/$tripId/itinerary')({
  component: ItineraryPage,
  validateSearch: (search: Record<string, unknown>) => {
    const mode = search.mode as string | undefined;
    return {
      mode: (['story', 'timeline', 'map'].includes(mode || '') ? mode : 'story') as ItineraryMode,
      token: (search.token as string) || undefined,
    };
  },
});

function ItineraryPage() {
  const { tripId } = Route.useParams();
  const { mode, token } = Route.useSearch();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const isPublicAccess = !!token;

  const authenticatedQuery = useTrip(tripId);
  const sharedQuery = useSharedTrip(tripId, token);

  const isAuthenticated = !!currentUser;
  const { data: trip, isLoading, error } = isPublicAccess
    ? sharedQuery
    : authenticatedQuery;

  const handleModeChange = (newMode: string) => {
    navigate({
      to: '/trip/$tripId/itinerary',
      params: { tripId },
      search: { mode: newMode as ItineraryMode, token },
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-muted-foreground text-lg">
            Loading your itinerary...
          </p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return <NotFound />;
  }

  const modes = [
    { value: 'story' as const, icon: BookOpen, label: 'Story' },
    { value: 'timeline' as const, icon: List, label: 'Timeline' },
    { value: 'map' as const, icon: MapPin, label: 'Map' },
  ];

  return (
    <>
      {/* Floating pill toolbar */}
      <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-black/40 backdrop-blur-lg rounded-full px-2 py-1.5 shadow-lg">
        {isAuthenticated && !isPublicAccess && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-white hover:bg-white/20"
            onClick={() =>
              navigate({
                to: '/trip/$tripId/edit',
                params: { tripId },
              })
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {isAuthenticated && !isPublicAccess && (
          <div className="w-px h-5 bg-white/20" />
        )}
        {modes.map(({ value, icon: Icon, label }) => (
          <button
            key={value}
            onClick={() => handleModeChange(value)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors text-white/70 hover:text-white hover:bg-white/10',
              mode === value && 'bg-white/20 text-white',
            )}
          >
            <Icon className="h-4 w-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Content — each mode fills the page */}
      <div
        className={cn(
          mode === 'map' && 'h-screen',
          mode === 'timeline' && 'min-h-screen bg-background',
          mode === 'story' && 'min-h-screen',
        )}
      >
        {mode === 'story' && <StoryView trip={trip} />}
        {mode === 'timeline' && <TraditionalView trip={trip} />}
        {mode === 'map' && <MapView trip={trip} />}
      </div>
    </>
  );
}
